import React, { useState, useRef, useEffect } from 'react';
import { X, Mail, Phone, MessageSquare, Calendar, Users, Clock, CheckCircle, Camera } from 'lucide-react';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface NightlifePackage {
  id: string;
  title: string;
  price: number;
  duration: string;
  image: string;
  description: string;
  highlights: string[];
}

interface NightlifeBookingFormProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackage: NightlifePackage | null;
}

const NightlifeBookingForm: React.FC<NightlifeBookingFormProps> = ({ 
  isOpen, 
  onClose, 
  selectedPackage 
}) => {
  const [startDate, setStartDate] = useState<Date | null>(new Date());
  const [participants, setParticipants] = useState(1);
  const [includePhotoPackage, setIncludePhotoPackage] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    specialRequests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'auto';
    };
  }, [isOpen, onClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateTotal = () => {
    if (!selectedPackage) return 0;
    
    let total = selectedPackage.price * participants;
    
    // Apply group discounts
    if (participants >= 7) {
      total = total * 0.8; // 20% discount
    } else if (participants >= 4) {
      total = total * 0.85; // 15% discount
    }
    
    // Add photo package if selected
    if (includePhotoPackage) {
      total += 30; // $30 for photo package
    }
    
    return total;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      setShowTermsError(true);
      return;
    }
    
    if (!selectedPackage) return;
    
    setIsSubmitting(true);
    
    try {
      // Prepare form data for Netlify
      const formBody = {
        'form-name': 'nightlife-booking',
        ...formData,
        package: selectedPackage.title,
        date: startDate?.toLocaleDateString() || '',
        participants: participants.toString(),
        photoPackage: includePhotoPackage ? 'Yes' : 'No',
        totalPrice: calculateTotal().toFixed(2),
        discount: participants >= 7 ? '20%' : participants >= 4 ? '15%' : 'None',
        termsAccepted: 'Yes'
      };

      // Submit to Netlify forms
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formBody).toString()
      });

      if (response.ok) {
        setIsSuccess(true);
        
        // Reset form after 3 seconds and close modal
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            specialRequests: ''
          });
          setParticipants(1);
          setStartDate(new Date());
          setIncludePhotoPackage(false);
          setIsSuccess(false);
          setTermsAccepted(false);
          onClose();
        }, 3000);
      } else {
        throw new Error('Form submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error processing your booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !selectedPackage) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-lg shadow-xl w-full max-w-[260px] overflow-hidden"
      >
        {/* Header with image */}
        <div className="relative h-20">
          <img 
            src={selectedPackage.image} 
            alt={selectedPackage.title} 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
          <button 
            onClick={onClose}
            className="absolute top-1 right-1 bg-white/80 p-0.5 rounded-full hover:bg-white transition-colors"
          >
            <X className="h-3 w-3 text-gray-700" />
          </button>
          <div className="absolute bottom-1 left-1 text-white">
            <h3 className="text-xs font-bold">{selectedPackage.title}</h3>
            <div className="flex items-center">
              <Clock className="h-2 w-2 text-purple-400 mr-0.5" />
              <span className="text-white/80 text-[8px]">{selectedPackage.duration}</span>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-2">
          {isSuccess ? (
            <div className="text-center py-4">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="h-4 w-4 text-green-600" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 mb-1">Booking Confirmed!</h3>
              <p className="text-xs text-gray-600">
                Thank you for booking with us.
              </p>
            </div>
          ) : (
            <form name="nightlife-booking" method="POST" data-netlify="true" onSubmit={handleSubmit} className="space-y-2">
              <input type="hidden" name="form-name" value="nightlife-booking" />
              <input type="hidden" name="package" value={selectedPackage.title} />
              
              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-1 top-1/2 transform -translate-y-1/2 h-2.5 w-2.5 text-gray-400" />
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-1 py-1 text-xs border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Phone *</label>
                  <div className="relative">
                    <Phone className="absolute left-1 top-1/2 transform -translate-y-1/2 h-2.5 w-2.5 text-gray-400" />
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full pl-4 pr-1 py-1 text-xs border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Date *</label>
                  <div className="relative">
                    <Calendar className="absolute left-1 top-1/2 transform -translate-y-1/2 h-2.5 w-2.5 text-gray-400 z-10" />
                    <DatePicker
                      selected={startDate}
                      onChange={(date) => setStartDate(date)}
                      minDate={new Date()}
                      className="w-full pl-4 pr-1 py-1 text-xs border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                      placeholderText="Select date"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Participants *</label>
                  <div className="flex items-center">
                    <button 
                      type="button"
                      onClick={() => setParticipants(prev => Math.max(1, prev - 1))}
                      className="px-1 py-0.5 border border-gray-300 rounded-l-md bg-gray-50 hover:bg-gray-100 text-xs"
                    >-</button>
                    <input 
                      type="number" 
                      name="participants"
                      value={participants}
                      readOnly
                      className="w-8 py-0.5 px-1 text-center text-xs border-t border-b border-gray-300"
                    />
                    <button 
                      type="button"
                      onClick={() => setParticipants(prev => prev + 1)}
                      className="px-1 py-0.5 border border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 text-xs"
                    >+</button>
                  </div>
                </div>
              </div>

              {/* Photo Package Add-on */}
              <div className="bg-purple-50 p-2 rounded-md">
                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="photoPackage"
                    checked={includePhotoPackage}
                    onChange={() => setIncludePhotoPackage(!includePhotoPackage)}
                    className="mt-0.5 h-3 w-3 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                  <div className="ml-2">
                    <label htmlFor="photoPackage" className="text-xs font-medium text-gray-700 flex items-center">
                      <Camera className="h-3 w-3 text-purple-600 mr-0.5" />
                      Photo Package
                    </label>
                    <p className="text-[8px] text-gray-500">Professional photographer ($30)</p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Special Requests</label>
                <div className="relative">
                  <MessageSquare className="absolute left-1 top-1.5 h-2.5 w-2.5 text-gray-400" />
                  <textarea
                    name="specialRequests"
                    value={formData.specialRequests}
                    onChange={handleInputChange}
                    rows={1}
                    className="w-full pl-4 pr-1 py-1 text-xs border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
                    placeholder="Any special requirements?"
                  ></textarea>
                </div>
              </div>

              {/* Terms and Conditions Checkbox */}
              <div className="flex items-start">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  checked={termsAccepted}
                  onChange={() => {
                    setTermsAccepted(!termsAccepted);
                    if (!termsAccepted) setShowTermsError(false);
                  }}
                  className="mt-0.5 h-3 w-3 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                />
                <div className="ml-2">
                  <label htmlFor="termsAccepted" className="text-[8px] text-gray-700">
                    I agree to the Terms & Conditions
                  </label>
                  {showTermsError && (
                    <p className="text-[8px] text-red-500 mt-0.5">
                      You must accept the terms and conditions
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-2 rounded-md text-[10px]">
                <div className="flex justify-between mb-0.5">
                  <span className="text-gray-600">Package Price</span>
                  <span>${selectedPackage.price} × {participants}</span>
                </div>
                {participants >= 4 && (
                  <div className="flex justify-between mb-0.5 text-green-600">
                    <span>Group Discount</span>
                    <span>-{participants >= 7 ? '20' : '15'}%</span>
                  </div>
                )}
                {includePhotoPackage && (
                  <div className="flex justify-between mb-0.5">
                    <span className="text-gray-600">Photo Package</span>
                    <span>$30</span>
                  </div>
                )}
                <div className="border-t border-gray-200 mt-0.5 pt-0.5 flex justify-between font-medium">
                  <span>Total</span>
                  <span>${calculateTotal().toFixed(2)}</span>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-1.5 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-xs flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  'Complete Booking'
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default NightlifeBookingForm;